import { type ActivityOf, getItem } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, DT, type RealtimeSim } from './realtime.ts';
import type { Fx } from './types.ts';

/** Past ±THROWN the rider is in the mud. */
export const THROWN = 100;
/** Seconds of warning before each buck on a horse of your own level; each level above it adds a little. */
const TELL = 0.35, TELL_PER_LEVEL = 0.004, TELL_MAX = 0.6;

/** How much of each kick reaches the rider: practice steadies it, quickly to level 20 and slowly after. */
export const steadiness = (level: number) => 1 - Math.min(0.28, 0.01 * Math.min(level, 20) + 0.001 * Math.max(0, level - 20));

export interface HorsemanshipSim extends RealtimeSim {
  /** Seconds in the saddle. */
  time: number;
  duration: number;
  /** Balance, −100 (thrown left) to +100 (thrown right). */
  lean: number;
  vel: number;
  /** The horse's tilt in degrees, for drawing; it eases back after each buck. */
  buckAngle: number;
  /** The horse gathers itself before a buck: the way it is about to throw you (−1 left, 1 right), or 0. */
  tell: -1 | 0 | 1;
  /** Seconds of warning before each buck. */
  tellLead: number;
}

/**
 * The Horse Fair: stay on a bucking horse until it calms. Hold lean-left / lean-right against each
 * lurch; over-correct and you pitch off the other side. The horse gathers itself a beat before each
 * buck, showing which way it will throw you. Ported from the Harrowmere prototype.
 */
export function horsemanshipSim(a: ActivityOf<'horsemanship'>, level: number, seed: string): HorsemanshipSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  const steady = steadiness(level);
  const horse = getItem(a.product).name.toLowerCase();
  // Each buck is drawn a buck ahead, so the horse can give it away.
  const draw = () => ({ dir: rng.chance(0.5) ? (-1 as const) : (1 as const), kick: rand(55, 120) });
  let nextBuck = 0.8, sum = 0, buck = draw();
  const fx = (text: string, tone: Fx['tone'], cue?: string, power?: number) => sim.fx.push({ tick: sim.tick, text, tone, cue, power });

  const sim: HorsemanshipSim = {
    tick: 0,
    result: null,
    fx: [],
    time: 0,
    duration: a.duration,
    lean: 0,
    vel: 0,
    buckAngle: 0,
    tell: 0,
    tellLead: Math.min(TELL_MAX, TELL + TELL_PER_LEVEL * Math.max(0, level - a.level)),
    step(mask) {
      if (sim.result) return;
      sim.tick++;
      sim.time += DT;
      if (sim.time >= nextBuck) {
        sim.vel += buck.dir * buck.kick * a.wild * steady;
        fx('', 'muted', 'buck', buck.kick / 120);
        buck = draw();
        nextBuck = sim.time + rand(0.5, 1.3) / a.wild;
        sim.buckAngle = rand(-12, 12);
      }
      sim.tell = nextBuck - sim.time <= sim.tellLead ? buck.dir : 0;
      const input = (mask & BTN.right ? 1 : 0) - (mask & BTN.left ? 1 : 0);
      sim.vel += (sim.lean * 1.2 + input * 210) * DT;
      sim.vel *= 1 - Math.min(1, 1.5 * DT);
      sim.lean += sim.vel * DT;
      sim.buckAngle *= 1 - Math.min(1, 4 * DT);
      sum += (1 - Math.min(1, Math.abs(sim.lean) / THROWN)) * DT;

      if (Math.abs(sim.lean) >= THROWN) {
        fx('Thrown into the mud!', 'bad', 'thrown');
        sim.result = {
          score: (sim.time / a.duration) * 0.3,
          title: 'Thrown!',
          text: `The ${a.name.toLowerCase()} throws you after ${sim.time.toFixed(1)}s.`,
          breakdown: [['Time in saddle', sim.time / a.duration]],
          yield: null,
          coin: 0,
          xpMult: 1,
          feats: ['thrown'],
        };
      } else if (sim.time >= a.duration) {
        const balance = sum / sim.time;
        fx('The horse calms. Tamed!', 'good', 'tamed');
        sim.result = {
          score: balance,
          title: `You break the ${horse}`,
          text: 'It stands blowing and trembling, then nuzzles your hand.',
          breakdown: [['Balance', balance]],
          yield: { itemKey: a.product, quantity: 1, graded: true },
          coin: 0,
          xpMult: 1,
          feats: balance >= 0.9 ? ['steady-seat'] : [],
        };
      }
    },
  };
  return sim;
}
